/**
 * ============================================================================
 * RESOLUTION CENTER COMPONENT
 * ============================================================================
 * 
 * Main Resolution Screen
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispute } from '../../hooks/useDispute';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Button from '../common/Button';
import Card from '../common/Card';
import Spinner from '../common/Spinner';
import StatusBadge from '../common/StatusBadge';
import MakeOffer from './MakeOffer';
import NegotiationTimer from './NegotiationTimer';
import DisputeChat from './DisputeChat';
import DisputeTimeline from './DisputeTimeline';

/**
 * Resolution Center Component
 * @returns {JSX.Element}
 */
const ResolutionCenter = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getDispute, sendMessage, loading } = useDispute();
  const { showError } = useToast();
  const { formatDate } = useI18n();

  const [dispute, setDispute] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showMakeOffer, setShowMakeOffer] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const loadDispute = async () => {
      try {
        setIsLoading(true);
        const data = await getDispute(id);
        setDispute(data);
      } catch (error) {
        showError('Failed to load dispute');
        console.error('Load dispute error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      loadDispute();
    }
  }, [id, getDispute, showError]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    try {
      await sendMessage(id, message);
      setMessage('');
      // Reload dispute to get new messages
      const data = await getDispute(id);
      setDispute(data);
    } catch (error) {
      showError(error.message || 'Failed to send message');
    }
  };

  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!dispute) {
    return null;
  }

  const statusColors = {
    open: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    negotiating: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    escalated: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  };

  return (
    <>
        {/* Header */}
        <div className="mb-8 animate-fadeInUp">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-all duration-200 group transform hover:scale-105"
          >
            <svg
              className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="font-medium">Back</span>
          </button>
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
                Resolution Center
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                Issue #{dispute.id || dispute.shortCode}
              </p>
            </div>
            <StatusBadge
              variant={
                dispute.status === 'resolved'
                  ? 'success'
                  : dispute.status === 'negotiating'
                  ? 'info'
                  : dispute.status === 'escalated'
                  ? 'warning'
                  : 'default'
              }
              size="lg"
            >
              {dispute.status?.toUpperCase() || 'OPEN'}
            </StatusBadge>
          </div>
        </div>

        {showMakeOffer ? (
          <div className="animate-scaleIn">
            <MakeOffer
              disputeId={id}
              onClose={() => setShowMakeOffer(false)}
              onSuccess={() => {
                setShowMakeOffer(false);
                // Reload dispute
                getDispute(id).then(setDispute);
              }}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Timeline */}
              <DisputeTimeline dispute={dispute} events={dispute.events} />

              {/* Chat */}
              <div className="h-[600px]">
                <DisputeChat
                  disputeId={id}
                  messages={dispute.messages || []}
                  onNewMessage={() => {
                    // Reload dispute to get new messages
                    getDispute(id).then(setDispute);
                  }}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Negotiation Timer */}
              {dispute.openedAt && (
                <div className="animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
                  <NegotiationTimer
                    openedAt={dispute.openedAt}
                    onExpire={() => {
                      // Reload dispute when timer expires
                      getDispute(id).then(setDispute);
                    }}
                  />
                </div>
              )}

              {/* Actions Card */}
              <Card
                variant="glass"
                hover
                className="animate-fadeInUp"
                style={{ animationDelay: '0.2s' }}
                header={
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <span className="text-xl">⚡</span>
                    Quick Actions
                  </h3>
                }
              >
                <div className="space-y-3">
                  <Button
                    variant="gradient"
                    fullWidth
                    size="lg"
                    onClick={() => setShowMakeOffer(true)}
                    className="shadow-lg hover:shadow-xl transition-all"
                  >
                    💰 Make Offer
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    size="lg"
                    onClick={() => navigate(`/disputes/${id}/mediation`)}
                    className="font-semibold"
                  >
                    ⚖️ Request Mediation
                    <span className="ml-2 text-xs opacity-75">(KES 500)</span>
                  </Button>
                </div>
              </Card>

              {/* Dispute Info */}
              <Card
                variant="glass"
                className="animate-fadeInUp"
                style={{ animationDelay: '0.3s' }}
                header={
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <span className="text-xl">ℹ️</span>
                    Dispute Details
                  </h3>
                }
              >
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Issue Type:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white capitalize">
                      {dispute.issueType?.replace('_', ' ') || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Desired Resolution:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white capitalize">
                      {dispute.desiredResolution?.replace('_', ' ') || 'N/A'}
                    </span>
                  </div>
                  {dispute.refundPercentage && (
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Refund Request:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">
                        {dispute.refundPercentage}%
                      </span>
                    </div>
                  )}
                </div>
              </Card>

              {/* Tips */}
              <Card
                variant="glass"
                className="bg-gradient-to-br from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 border-2 border-blue-200 dark:border-blue-800 animate-fadeInUp"
                style={{ animationDelay: '0.4s' }}
              >
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-start gap-2">
                  <span className="text-lg">💡</span>
                  <span>
                    Try to reach an agreement before the timer expires. Mediation is available if
                    needed.
                  </span>
                </p>
              </Card>
            </div>
          </div>
        )}
    </>
  );
};

export default ResolutionCenter;
